import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../lib/queryKeys.js';
import {
  Plus,
  Calendar,
  CalendarDays,
  ClipboardList,
  Check,
  X,
  ArrowRight,
  Layers,
  ChevronDown,
  Pencil,
  RotateCcw,
} from 'lucide-react';
import type {
  AssignmentPurpose,
  CalendarAssignmentRow,
} from '@gutterinv/shared';
import {
  getCalendarAssignments,
  postCalendarAssignment,
  deleteCalendarAssignment,
  setAssignmentComplete,
} from '../api/client.js';
import { PeopleMultiSelect } from './PeopleMultiSelect.js';
import { AssignmentEditModal } from './AssignmentEditModal.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { cn } from '../lib/utils.js';
import { formatDate, todayIso, isPastDate } from '../lib/format.js';

/** Format a Date as YYYY-MM-DD (the format <input type="date"> wants). */
function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Generic scheduling card for a job. Renders either install or estimate
 * assignments for the given job depending on `purpose` prop.
 *
 * Within a single purpose, one crew per job is enforced — UI locks the
 * crew picker once an assignment exists. Across purposes, crews are
 * independent (Alpha can estimate while Bravo installs the same job).
 *
 * 2-day option is install-only (estimate visits are ~30 min, single day).
 */
export function JobScheduleSection({
  jobId,
  purpose = 'install',
  readOnly = false,
  productLine,
  insideDrawer = false,
  onCloseDrawer,
  heading,
  canSchedule = true,
}: {
  jobId: string;
  purpose?: AssignmentPurpose;
  /** Overrides the internal "Schedule estimate"/"Schedule" h3 — e.g. the
   *  two-halves Schedule card passes "Estimate"/"Install". */
  heading?: string;
  /** Gates the "Schedule a crew" button/form. The two-halves card sets this
   *  false on the estimate half once the job is past `created` so it stops
   *  offering new estimate scheduling but still shows existing rows + revert. */
  canSchedule?: boolean;
  /** When true, shows the existing assignment list as history but
   *  hides the X (unassign) button and the "+ Schedule Job" button.
   *  Used for post-install jobs (installed / invoiced / completed)
   *  and disqualified jobs — the worker rejects POST/PATCH/DELETE
   *  on those, so the UI matches the server contract. */
  readOnly?: boolean;
  /** Job product_line. Insulation jobs hide the "Schedule Job" CTA
   *  in favor of the Install Phases card; gutter/undefined keeps
   *  existing behavior. Added migration 0043. */
  productLine?: 'gutter' | 'insulation';
  /** Set true when this card is rendered inside the JobDetailPanel
   *  drawer. Used by the insulation deep-link CTA to close the drawer
   *  before navigating. Same-page navigate-with-hash doesn't scroll
   *  on React Router by default; on JobDetailPage (insideDrawer=false)
   *  the Phases card is already visible above the fold, so the CTA
   *  is hidden entirely. */
  insideDrawer?: boolean;
  /** Closes the parent drawer. Required when insideDrawer=true so
   *  the insulation CTA can dismiss the drawer before navigating. */
  onCloseDrawer?: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isInsulation = productLine === 'insulation' && purpose === 'install';
  // Phase 1.3 Item 12: per-row expand/collapse state for insulation rows.
  // Toggling via chevron reveals an items+qty panel below the row. Set
  // pattern (replace, don't mutate) so React picks up the change.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleExpand = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const [showForm, setShowForm] = useState(false);
  const [personIds, setPersonIds] = useState<string[]>([]);
  // Multi-day install support (0025): start + end. Estimate forms render
  // a single date input (start === end on submit).
  const [startDate, setStartDate] = useState(() => toIsoDate(new Date()));
  const [endDate, setEndDate] = useState(() => toIsoDate(new Date()));

  const isEstimate = purpose === 'estimate';

  const { data: allAssignments } = useQuery({
    queryKey: qk.calendar.assignmentsAllDates(),
    queryFn: () => getCalendarAssignments('2020-01-01', '2030-01-01'),
    staleTime: 30_000,
  });

  // Filter to this job AND this purpose. Crew-lock + extend-day logic
  // apply within-purpose only; install and estimate rows are independent.
  const jobAssignments = useMemo(() => {
    if (!allAssignments) return [];
    return allAssignments.filter(
      (a: CalendarAssignmentRow) => a.job.id === jobId && a.purpose === purpose,
    );
  }, [allAssignments, jobId, purpose]);

  // Phase 2.2: people are repeatable across assignments, so the legacy
  // "crew already taken" exclusion + auto-advance are gone. People are
  // picked directly via <PeopleMultiSelect>.

  const earliestExistingDate = useMemo(() => {
    if (jobAssignments.length === 0) return null;
    return jobAssignments
      .map((a: CalendarAssignmentRow) => a.assignedDateStart)
      .sort()[0]!;
  }, [jobAssignments]);

  useEffect(() => {
    if (showForm && earliestExistingDate) {
      if (startDate !== earliestExistingDate) setStartDate(earliestExistingDate);
      if (endDate !== earliestExistingDate) setEndDate(earliestExistingDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, earliestExistingDate]);

  const assignMutation = useMutation({
    mutationFn: postCalendarAssignment,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'assignments'] });
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'unassigned'] });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      setShowForm(false);
    },
  });

  const unassignMutation = useMutation({
    mutationFn: deleteCalendarAssignment,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'assignments'] });
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'unassigned'] });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['job', jobId] });
    },
  });

  // Same 4-key invalidation the assign/unassign mutations use — completing an
  // estimate changes job.status (so the job header + the calendar 'unassigned'
  // buckets must refresh), and the row's completedAt drives the strikethrough.
  const invalidateSchedule = () => {
    void queryClient.invalidateQueries({ queryKey: ['calendar', 'assignments'] });
    void queryClient.invalidateQueries({ queryKey: ['calendar', 'unassigned'] });
    void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    void queryClient.invalidateQueries({ queryKey: ['job', jobId] });
  };

  // Estimate-visit completion (estimate purpose only). Complete advances the job
  // created→estimated when a confirmed BoM exists; revert is backend-blocked once
  // an install is scheduled (surfaced via the alert below).
  const completeMutation = useMutation({
    mutationFn: (id: string) => setAssignmentComplete(id, true),
    onSuccess: invalidateSchedule,
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Could not mark complete'),
  });
  const revertMutation = useMutation({
    mutationFn: (id: string) => setAssignmentComplete(id, false),
    onSuccess: invalidateSchedule,
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Could not revert'),
  });

  const startInvalid = isPastDate(startDate);
  const endInvalid = !isEstimate && !!endDate && endDate < startDate;
  const saveDisabled =
    assignMutation.isPending ||
    personIds.length === 0 ||
    !startDate ||
    (!isEstimate && !endDate) ||
    startInvalid ||
    endInvalid;

  const handleSave = () => {
    if (personIds.length === 0 || !startDate) return;
    // Estimates collapse to single-day on submit; install always sends
    // explicit start + end.
    const effectiveEnd = isEstimate ? startDate : endDate;
    if (!effectiveEnd) return;
    assignMutation.mutate({
      jobId,
      personIds,
      startDate,
      endDate: effectiveEnd,
      purpose,
    });
  };

  const title = heading ?? (isEstimate ? 'Schedule estimate' : 'Schedule');
  const HeaderIcon = isEstimate ? ClipboardList : Calendar;
  const buttonLabel = isEstimate ? 'Schedule a crew to estimate' : 'Schedule Job';
  const emptyHint = isEstimate
    ? 'Office has not scheduled an estimate visit yet. The crew will see this job on /estimate once a visit is scheduled.'
    : null;

  // Earliest assignment date for the calendar deep-link, if any. Falls
  // back to "today" so the calendar always opens on a reasonable anchor.
  const calendarLinkDate = earliestExistingDate ?? toIsoDate(new Date());

  // Edit an existing assignment in place via the shared calendar modal —
  // same edit/lock/revert behavior as the calendar (completed rows lock).
  const [editingAssignment, setEditingAssignment] = useState<CalendarAssignmentRow | null>(null);

  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="eyebrow flex items-center gap-1.5">
          <HeaderIcon className="h-3 w-3" />
          {title}
        </h3>
        <Link
          to={`/calendar?date=${calendarLinkDate}&jobId=${jobId}`}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-0.5 text-[0.6875rem] font-medium text-muted-foreground hover:bg-accent/40 hover:text-foreground transition-colors"
          title="Open this job's date in the calendar"
        >
          <CalendarDays className="h-3 w-3" />
          Open in calendar
        </Link>
      </div>

      {jobAssignments.length > 0 && (
        <div className="mb-2 space-y-1">
          {jobAssignments.map((a: CalendarAssignmentRow) => {
            const completed = a.completedAt !== null;
            // Phase 2.5: show the assigned PEOPLE, not the (representative)
            // crew. Fall back to the crew name only when an assignment has
            // no people. 3+ people truncate to "A, B +N" with the full list
            // in the title; the color dot stays (representative-preset color,
            // matches the calendar).
            const people = a.people;
            const peopleLabel =
              people.length === 0
                ? a.crew.name
                : people.length <= 2
                  ? people.map((p) => p.name).join(', ')
                  : `${people[0]!.name}, ${people[1]!.name} +${people.length - 2}`;
            const peopleTitle =
              people.length > 0 ? people.map((p) => p.name).join(', ') : undefined;
            const phasePill = a.phase
              ? `Phase ${a.phase.phaseNumber} · ${a.phase.itemCount} item${a.phase.itemCount === 1 ? '' : 's'}`
              : null;
            // Migration 0043: insulation assignments are owned by the
            // Phases card. The unassign affordance is hidden here; office
            // deletes via the Phases card so item-pull reversal logic runs.
            const hideUnassign = readOnly || isInsulation;
            // Phase 1.3 Item 12: chevron + items panel for insulation rows.
            const hasItemsToShow = a.phase != null && (a.phase.items?.length ?? 0) > 0;
            const isExpanded = expandedRows.has(a.id);
            return (
              <div
                key={a.id}
                className={cn(
                  'rounded-md border',
                  completed
                    ? 'border-primary/30 bg-primary/[0.04]'
                    : 'border-border',
                )}
              >
                <div className="flex items-center justify-between gap-2 px-2.5 py-1.5">
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    {hasItemsToShow && (
                      <button
                        type="button"
                        onClick={() => toggleExpand(a.id)}
                        className="rounded p-0.5 text-muted-foreground hover:text-foreground transition-colors"
                        title={isExpanded ? 'Collapse items' : 'Expand to see items + qty'}
                        aria-expanded={isExpanded}
                      >
                        <ChevronDown
                          className={cn(
                            'h-3.5 w-3.5 transition-transform',
                            isExpanded ? '' : '-rotate-90',
                          )}
                        />
                      </button>
                    )}
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: a.crew.color }}
                    />
                    <span
                      className={cn(
                        'font-medium text-foreground',
                        completed && 'text-muted-foreground line-through',
                      )}
                      title={peopleTitle}
                    >
                      {peopleLabel}
                    </span>
                    <span className={cn('text-muted-foreground', completed && 'line-through')}>
                      {a.assignedDateStart === a.assignedDateEnd
                        ? formatDate(a.assignedDateStart)
                        : `${formatDate(a.assignedDateStart)} – ${formatDate(a.assignedDateEnd)}`}
                    </span>
                    {phasePill && (
                      <span
                        className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary/[0.06] px-1.5 py-0.5 text-[0.6875rem] font-medium text-primary"
                        title={a.phase!.itemsSummary || undefined}
                      >
                        <Layers className="h-3 w-3" />
                        {phasePill}
                      </span>
                    )}
                    {completed && (
                      <span
                        className="flex h-5 w-5 items-center justify-center rounded-full bg-success text-success-foreground"
                        title="Estimate complete"
                        aria-label="Complete"
                      >
                        <Check size={12} strokeWidth={3} />
                      </span>
                    )}
                  </div>
                  {!hideUnassign && (
                    <div className="flex items-center gap-0.5">
                      {completed ? (
                        // Completed estimate → revert only (RotateCcw), mirroring the
                        // insulation Install-Phase uncomplete. (Install rows complete via
                        // the job 'installed' transition + go readOnly, so they don't reach
                        // this branch.)
                        isEstimate && (
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                confirm(
                                  'Revert this estimate to not-complete? This may move the job back to Created.',
                                )
                              ) {
                                revertMutation.mutate(a.id);
                              }
                            }}
                            disabled={revertMutation.isPending}
                            title="Revert estimate to not-complete"
                            aria-label="Revert estimate to not-complete"
                            className="flex h-7 w-7 items-center justify-center rounded text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                          >
                            <RotateCcw size={14} />
                          </button>
                        )
                      ) : (
                        <>
                          {/* Estimate-only: green complete check (advances created→estimated
                              when a confirmed BoM exists). Install completion is status-driven
                              — no per-assignment toggle — so no check on install rows. */}
                          {isEstimate && (
                            <button
                              type="button"
                              onClick={() => completeMutation.mutate(a.id)}
                              disabled={completeMutation.isPending}
                              title="Mark estimate complete — advances to Estimated when a confirmed BoM exists"
                              aria-label="Mark estimate complete"
                              className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-success bg-white text-success transition-colors hover:bg-success/10 disabled:opacity-50"
                            >
                              <Check size={12} strokeWidth={3} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setEditingAssignment(a)}
                            title="Edit this scheduled visit"
                            aria-label="Edit this scheduled visit"
                            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => unassignMutation.mutate(a.id)}
                            disabled={unassignMutation.isPending}
                            title="Unassign"
                            aria-label="Unassign"
                            className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                {hasItemsToShow && isExpanded && (
                  <ul className="border-t border-border/60 px-2.5 py-1.5 space-y-0.5">
                    {a.phase!.items!.map((it, idx) => (
                      <li
                        key={`${idx}-${it.name}`}
                        className="truncate text-[0.6875rem] text-muted-foreground pl-6"
                        title={`${it.name} × ${String(it.quantity)} ${it.unit}`}
                      >
                        {it.name} × {String(it.quantity)} {it.unit}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {unassignMutation.error && !readOnly && (
        <div className="mb-2 text-xs text-destructive">
          {unassignMutation.error instanceof Error
            ? unassignMutation.error.message
            : 'Unassign failed'}
        </div>
      )}

      {/* Read-only mode: stop after the assignment list. The
          worker rejects POST/PATCH/DELETE on installed/invoiced/
          completed/disqualified jobs, so the UI matches by hiding
          the "+ Schedule" button and the unassign affordance. */}
      {readOnly && jobAssignments.length === 0 && (
        <p className="text-[0.6875rem] text-muted-foreground">
          No {purpose} assignments were recorded for this job.
        </p>
      )}
      {readOnly ? null : jobAssignments.length === 0 && emptyHint && !showForm ? (
        <p className="mb-2 text-[0.6875rem] text-muted-foreground">{emptyHint}</p>
      ) : null}

      {readOnly ? null : isInsulation ? (
        // Migration 0043: insulation jobs schedule through the Install
        // Phases card so the phases row + items get populated atomically.
        // Inside the drawer: close + navigate to JobDetailPage with the
        // #phases hash. On JobDetailPage itself: hide the CTA (the
        // Phases card is already visible above the fold; the deep-link
        // would be a redundant click + same-page hash navigation doesn't
        // scroll on React Router by default).
        insideDrawer ? (
          <button
            type="button"
            onClick={() => {
              onCloseDrawer?.();
              navigate(`/jobs/${jobId}#phases`);
            }}
            className="w-full rounded-md border border-primary/30 bg-primary/[0.04] px-3 py-2 text-left transition-colors hover:bg-primary/[0.08]"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs font-medium text-primary">
                <Layers className="h-3.5 w-3.5 shrink-0" />
                Schedule via Install Phases
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-primary" />
            </div>
            <p className="mt-0.5 text-[0.6875rem] text-muted-foreground">
              Insulation jobs schedule one phase at a time so each visit can be invoiced separately.
            </p>
          </button>
        ) : null
      ) : !canSchedule ? null : !showForm ? (
        <>
          <Button
            variant="outline"
            size="sm"
            className="w-full text-xs"
            onClick={() => setShowForm(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            {buttonLabel}
          </Button>
        </>
      ) : (
        <div className="space-y-2 rounded-md border border-border bg-secondary/30 p-2.5">
          <div>
            <label className="eyebrow mb-1 block">People</label>
            <PeopleMultiSelect value={personIds} onChange={setPersonIds} />
          </div>

          {isEstimate ? (
            <div>
              <label className="eyebrow mb-1 block">Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                min={todayIso()}
                aria-invalid={startInvalid}
                className={cn(
                  'h-8 text-xs',
                  startInvalid && 'border-destructive focus-visible:ring-destructive',
                )}
                disabled={assignMutation.isPending}
              />
              {startInvalid && (
                <p className="mt-1 text-[0.6875rem] text-destructive">
                  Date cannot be in the past.
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="eyebrow mb-1 block">Start Date</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    const v = e.target.value;
                    setStartDate(v);
                    // Keep the range valid: if the new start exceeds the
                    // current end, snap end forward to match.
                    if (endDate && v > endDate) setEndDate(v);
                  }}
                  min={todayIso()}
                  aria-invalid={startInvalid}
                  className={cn(
                    'h-8 text-xs',
                    startInvalid && 'border-destructive focus-visible:ring-destructive',
                  )}
                  disabled={assignMutation.isPending}
                />
              </div>
              <div>
                <label className="eyebrow mb-1 block">End Date</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate || todayIso()}
                  aria-invalid={endInvalid}
                  className={cn(
                    'h-8 text-xs',
                    endInvalid && 'border-destructive focus-visible:ring-destructive',
                  )}
                  disabled={assignMutation.isPending || !startDate}
                />
              </div>
              {(startInvalid || endInvalid) && (
                <p className="col-span-2 mt-1 text-[0.6875rem] text-destructive">
                  {startInvalid
                    ? 'Start date cannot be in the past.'
                    : 'End date must be on or after start date.'}
                </p>
              )}
            </div>
          )}

          {assignMutation.error && (
            <div className="text-xs text-destructive">
              {assignMutation.error instanceof Error
                ? assignMutation.error.message
                : 'Assign failed'}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              size="sm"
              className="h-7 flex-1 text-xs"
              disabled={saveDisabled}
              onClick={handleSave}
            >
              {assignMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={assignMutation.isPending}
              onClick={() => setShowForm(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Install completion is the job 'installed' status transition, not a
          per-assignment toggle — so install rows have no green check. Say so. */}
      {!isEstimate && !isInsulation && !readOnly && (
        <p className="mt-2 text-[0.6875rem] text-muted-foreground">
          Install completes when the job is marked installed.
        </p>
      )}

      {editingAssignment && (
        <AssignmentEditModal
          assignment={editingAssignment}
          onClose={() => setEditingAssignment(null)}
        />
      )}
    </section>
  );
}
